import { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { fmtMoney } from "@/lib/pricing";
import { getPublicBaseUrl } from "@/lib/publicBase";
import { CheckCircle2, AlertTriangle, Loader2, Lock, ExternalLink } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Public page — DOES NOT use the authenticated axios instance
const pub = axios.create({ baseURL: API_BASE, timeout: 30000 });

function fmtErr(e) {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  return e?.message || "Request failed";
}

export default function PublicProposal() {
  const { token } = useParams();
  const [search] = useSearchParams();
  const paid = search.get("paid") === "1";
  const canceled = search.get("canceled") === "1";

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [status, setStatus] = useState(null);

  const [signerName, setSignerName] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [signing, setSigning] = useState(false);
  const [signError, setSignError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await pub.get(`/proposal/${token}`);
      setData(data);
    } catch (e) {
      setError(fmtErr(e));
    } finally {
      setLoading(false);
    }
  }, [token]);

  const pollStatus = useCallback(async () => {
    try {
      const { data } = await pub.get(`/proposal/${token}/status`);
      setStatus(data);
      return data;
    } catch {
      return null;
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  // If we returned from Stripe success, poll status until paid
  useEffect(() => {
    if (!paid) return;
    let cancelled = false;
    const loop = async () => {
      for (let i = 0; i < 20; i++) {
        if (cancelled) return;
        const s = await pollStatus();
        if (s?.status === "paid") return;
        await new Promise((r) => setTimeout(r, 2000));
      }
    };
    loop();
    return () => {
      cancelled = true;
    };
  }, [paid, pollStatus]);

  const submit = async (e) => {
    e.preventDefault();
    setSignError("");
    if (!signerName.trim() || signerName.trim().length < 2) {
      setSignError("Please type your full name.");
      return;
    }
    if (!agreed) {
      setSignError("You must agree to the terms to continue.");
      return;
    }
    setSigning(true);
    try {
      const { data } = await pub.post(`/proposal/${token}/sign`, {
        signer_name: signerName.trim(),
        agreed: true,
        origin_url: getPublicBaseUrl(),
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      setSignError(fmtErr(e));
      setSigning(false);
    }
  };

  const order = data?.order;
  const lead = data?.lead;
  const isPaid = status?.status === "paid" || order?.status === "paid";

  return (
    <div className="min-h-screen bg-bg text-ink flex flex-col" data-testid="public-proposal-page">
      <meta name="robots" content="noindex" />
      <header className="border-b border-line bg-bg-surface">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-baseline justify-between">
          <div>
            <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-cyan">
              Main St.
            </div>
            <div className="text-sm font-medium text-ink">Review & Activate</div>
          </div>
          <div className="text-[11px] font-mono uppercase tracking-widest text-ink-muted">
            {order?.proposal_token ? `Ref · ${order.proposal_token.slice(0, 6).toUpperCase()}` : ""}
          </div>
        </div>
      </header>

      <main className="flex-1">
        <div className="max-w-3xl mx-auto px-6 py-8">
          {canceled && !isPaid && (
            <Banner tone="warn" testId="canceled-banner">
              Checkout was canceled. You can re-sign and pay below.
            </Banner>
          )}
          {paid && !isPaid && (
            <Banner tone="info" testId="verifying-banner">
              <Loader2 size={12} className="animate-spin inline mr-1" />
              Verifying your payment with Stripe…
            </Banner>
          )}

          {loading ? (
            <LoadingSkeleton />
          ) : error ? (
            <ErrorState message={error} onRetry={load} />
          ) : data ? (
            <>
              {isPaid ? (
                <PaidState lead={lead} order={order} />
              ) : (
                <>
                  <IntroBlock lead={lead} />
                  <OrderSummary order={order} />
                  <AgreementBlock html={data.agreement_html} />
                  <SignForm
                    signerName={signerName}
                    setSignerName={setSignerName}
                    agreed={agreed}
                    setAgreed={setAgreed}
                    onSubmit={submit}
                    signing={signing}
                    error={signError}
                    orderStatus={order?.status}
                    paymentsReady={data.payments_ready}
                    oneTimeTotal={order?.one_time_total}
                    monthlyTotal={order?.monthly_total}
                  />
                </>
              )}
            </>
          ) : null}
        </div>
      </main>

      <footer className="border-t border-line bg-bg-surface">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between text-[11px] text-ink-muted font-mono">
          <div>Main Street Websites · a Zen Companies studio</div>
          <div className="flex items-center gap-1.5">
            <Lock size={11} />
            Secure signing & payment
          </div>
        </div>
      </footer>
    </div>
  );
}

function IntroBlock({ lead }) {
  return (
    <div className="mb-6">
      <div className="text-[11px] font-mono uppercase tracking-widest text-ink-muted">
        For
      </div>
      <h1 className="text-3xl font-semibold tracking-tight text-ink mt-1" data-testid="proposal-business-name">
        {lead?.name}
      </h1>
      <div className="text-sm text-ink-secondary mt-1">
        {lead?.owner && <span>Attn: {lead.owner} · </span>}
        {lead?.city}
      </div>
      {lead?.sample_url && (
        <a
          href={lead.sample_url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 mt-3 text-sm text-cyan hover:text-cyan-hover transition-colors"
          data-testid="proposal-sample-link"
        >
          <ExternalLink size={12} />
          Open the sample site again
        </a>
      )}
    </div>
  );
}

function OrderSummary({ order }) {
  const lines = order?.line_items || [];
  return (
    <section className="border border-line rounded-sm bg-bg-surface mb-6" data-testid="proposal-order-summary">
      <div className="px-5 py-3 border-b border-line">
        <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">
          Order summary
        </div>
      </div>
      <div className="divide-y divide-line">
        {lines.map((li, i) => (
          <div key={i} className="px-5 py-3 flex items-center gap-4" data-testid={`proposal-line-${i}`}>
            <div className="flex-1 min-w-0">
              <div className="text-sm text-ink">{li.label}</div>
            </div>
            <div className="text-right shrink-0">
              {li.one_time_subtotal > 0 && (
                <div className="text-sm text-ink tabular-nums font-mono">
                  {fmtMoney(li.one_time_subtotal)}
                </div>
              )}
              {li.monthly_subtotal > 0 && (
                <div className="text-xs text-ink-secondary tabular-nums font-mono">
                  {fmtMoney(li.monthly_subtotal)}/mo
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      <div className="px-5 py-4 border-t border-line grid grid-cols-2 gap-4 bg-cyan-muted">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">
            Due today
          </div>
          <div className="text-3xl font-semibold text-ink tabular-nums mt-1" data-testid="proposal-one-time-total">
            {fmtMoney(order?.one_time_total)}
          </div>
        </div>
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">
            Monthly thereafter
          </div>
          <div className="text-3xl font-semibold text-ink tabular-nums mt-1" data-testid="proposal-monthly-total">
            {fmtMoney(order?.monthly_total)}
            <span className="text-sm text-ink-secondary ml-1">/mo</span>
          </div>
        </div>
      </div>
    </section>
  );
}

function AgreementBlock({ html }) {
  return (
    <section
      className="border border-line rounded-sm bg-bg-surface p-6 mb-6"
      data-testid="proposal-agreement"
    >
      <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted mb-3">
        Service agreement
      </div>
      <div
        className="agreement-prose text-sm text-ink leading-relaxed max-h-[440px] overflow-y-auto pr-3 border border-line rounded-sm p-4 bg-bg"
        dangerouslySetInnerHTML={{ __html: html || "" }}
      />
      <style>{`
        .agreement-prose h2 { font-size: 1.125rem; font-weight: 600; color: #EFF4F2; margin-bottom: 0.75rem; }
        .agreement-prose h3 { font-size: 0.95rem; font-weight: 600; color: #EFF4F2; margin-top: 1rem; margin-bottom: 0.35rem; }
        .agreement-prose p { color: #A0B0AB; margin-bottom: 0.65rem; }
        .agreement-prose strong { color: #EFF4F2; }
      `}</style>
    </section>
  );
}

function SignForm({
  signerName,
  setSignerName,
  agreed,
  setAgreed,
  onSubmit,
  signing,
  error,
  orderStatus,
  paymentsReady,
  oneTimeTotal,
  monthlyTotal,
}) {
  return (
    <form onSubmit={onSubmit} className="border border-line rounded-sm bg-bg-surface p-6" data-testid="proposal-sign-form">
      <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted mb-3">
        Sign & activate
      </div>

      <label className="block mb-4">
        <div className="text-[11px] font-mono uppercase tracking-wider text-ink-muted mb-1.5">
          Type your full name to sign
        </div>
        <input
          value={signerName}
          onChange={(e) => setSignerName(e.target.value)}
          data-testid="proposal-signer-name-input"
          placeholder="e.g. Peter Anderson"
          className="w-full bg-bg border border-line rounded-sm px-3 py-2 text-sm text-ink placeholder:text-ink-muted focus:border-cyan transition-colors font-mono"
        />
      </label>

      <label className="flex items-start gap-3 cursor-pointer mb-4">
        <input
          type="checkbox"
          checked={agreed}
          onChange={(e) => setAgreed(e.target.checked)}
          data-testid="proposal-agree-checkbox"
          className="mt-0.5 h-4 w-4 accent-cyan shrink-0"
        />
        <span className="text-sm text-ink-secondary leading-relaxed">
          I have read and agree to the Service Agreement above. Typing my name and
          clicking &ldquo;I agree · Sign & pay&rdquo; is my legally binding electronic
          signature.
        </span>
      </label>

      {error && (
        <div
          className="mb-4 flex items-start gap-2 border rounded-sm px-3 py-2 text-xs"
          style={{
            backgroundColor: "rgba(239, 68, 68, 0.06)",
            borderColor: "rgba(239, 68, 68, 0.3)",
            color: "#F87171",
          }}
          data-testid="proposal-sign-error"
        >
          <AlertTriangle size={13} className="shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {!paymentsReady && (
        <div
          className="mb-4 flex items-start gap-2 border rounded-sm px-3 py-2 text-xs"
          style={{
            backgroundColor: "rgba(244, 184, 96, 0.06)",
            borderColor: "rgba(244, 184, 96, 0.35)",
            color: "#F4B860",
          }}
          data-testid="proposal-payments-not-ready"
        >
          <AlertTriangle size={13} className="shrink-0 mt-0.5" />
          <span>
            Payments aren&rsquo;t configured yet. Your rep will contact you shortly to
            complete activation.
          </span>
        </div>
      )}

      <button
        type="submit"
        disabled={signing || !paymentsReady}
        data-testid="proposal-sign-and-pay-btn"
        className="w-full flex items-center justify-center gap-2 bg-cyan text-bg font-medium py-3 rounded-sm hover:bg-cyan-hover transition-colors disabled:opacity-60 active:scale-[0.98]"
      >
        {signing ? (
          <>
            <Loader2 size={14} className="animate-spin" />
            Preparing checkout…
          </>
        ) : (
          <>
            <Lock size={14} />
            I agree · Sign & pay {fmtMoney(oneTimeTotal || 0)} today
            {monthlyTotal > 0 && ` + ${fmtMoney(monthlyTotal)}/mo`}
          </>
        )}
      </button>
      <div className="text-[11px] text-ink-muted text-center mt-2 font-mono">
        Card details are handled by Stripe. We never see or store your card number.
      </div>
    </form>
  );
}

function PaidState({ lead, order }) {
  return (
    <div className="border rounded-sm bg-bg-surface p-8" style={{ borderColor: "rgba(74, 222, 128, 0.35)" }} data-testid="proposal-paid-state">
      <div className="flex items-center gap-3">
        <CheckCircle2 size={22} style={{ color: "#4ADE80" }} />
        <h2 className="text-xl font-semibold text-ink">You&rsquo;re live.</h2>
      </div>
      <p className="text-sm text-ink-secondary mt-3 leading-relaxed max-w-lg">
        Setup collected, monthly subscription started. Your rep will reach out today
        to point the site at your domain, run through the customization round, and
        get {lead?.name} up.
      </p>
      <div className="mt-6 grid grid-cols-2 gap-4 max-w-md">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">
            Setup charged
          </div>
          <div className="text-xl text-ink tabular-nums font-mono mt-1">
            {fmtMoney(order?.one_time_total)}
          </div>
        </div>
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">
            Monthly subscription
          </div>
          <div className="text-xl text-ink tabular-nums font-mono mt-1">
            {fmtMoney(order?.monthly_total)}<span className="text-xs text-ink-secondary ml-1">/mo</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-4" data-testid="proposal-loading">
      {[...Array(3)].map((_, i) => (
        <div key={i} className="h-32 border border-line rounded-sm animate-pulse bg-bg-surface" />
      ))}
    </div>
  );
}

function ErrorState({ message, onRetry }) {
  return (
    <div
      className="border rounded-sm p-6"
      style={{
        backgroundColor: "rgba(239, 68, 68, 0.06)",
        borderColor: "rgba(239, 68, 68, 0.3)",
      }}
      data-testid="proposal-error"
    >
      <div className="flex items-start gap-3">
        <AlertTriangle size={18} style={{ color: "#F87171" }} className="shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="text-lg font-medium text-ink">Proposal not found</div>
          <div className="text-sm text-ink-secondary mt-2">{message}</div>
          <div className="text-xs text-ink-muted mt-3">
            If you&rsquo;re expecting a proposal, please ask your rep to re-send the
            link. Old links may expire when the order is regenerated.
          </div>
          <button
            onClick={onRetry}
            className="mt-4 text-xs border border-line text-ink hover:bg-bg-hover px-3 py-1.5 rounded-sm transition-colors"
            data-testid="proposal-retry-btn"
          >
            Retry
          </button>
        </div>
      </div>
    </div>
  );
}

function Banner({ tone, children, testId }) {
  const styles =
    tone === "warn"
      ? { bg: "rgba(244, 184, 96, 0.06)", border: "rgba(244, 184, 96, 0.35)", color: "#F4B860" }
      : { bg: "rgba(96, 165, 250, 0.06)", border: "rgba(96, 165, 250, 0.35)", color: "#60A5FA" };
  return (
    <div
      className="mb-4 border rounded-sm px-3 py-2 text-xs"
      style={{ backgroundColor: styles.bg, borderColor: styles.border, color: styles.color }}
      data-testid={testId}
    >
      {children}
    </div>
  );
}
